import { useParams, useNavigate } from "react-router-dom";
import { useAnime } from "../context/AnimeContext";
import { useAuth } from "../context/AuthContext";
import RatingStars from "../components/RatingStars";
import EpisodeList from "../components/EpisodeList";
import CharactersVoiceActors from "../components/CharactersVoiceActors";
import { useState, useEffect } from "react";
import {
  Sparkles,
  Users,
  MessageCircle,
  Heart,
  Star,
  Tv,
  Film,
  Calendar,
  Clock,
  ExternalLink,
  ArrowLeft,
  Play,
  ShoppingCart,
  Package,
  Book,
  Edit2,
  Save,
  X,
} from "lucide-react";
import { fetchAnimeReviews, fetchAnimeDetails } from "../services/api";
import {
  getAmazonAffiliateLink,
  getCrunchyrollAffiliateLink,
  trackAffiliateClick,
} from "../config/affiliate";
import { features } from "../config/features";
import {
  buildOptimisticLikeState,
  readCanonicalLikeState,
  reconcileLikeStateFromResponse,
} from "../utils/likeInteraction";

function RamenRatingSystem({ onRate, initialRating }) {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(initialRating);

  const handleClick = (rating, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const isLeftHalf = clickX < rect.width / 2;

    const finalRating = isLeftHalf ? rating - 0.5 : rating;
    setSelected(finalRating);
    onRate(finalRating);
  };

  const handleMouseMove = (rating, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const isLeftHalf = mouseX < rect.width / 2;

    setHovered(isLeftHalf ? rating - 0.5 : rating);
  };

  const getBowlDisplay = (num) => {
    const currentRating = hovered || selected;
    const fullBowls = Math.floor(currentRating);
    const hasHalf = currentRating % 1 >= 0.5;

    const isFull = num <= fullBowls;
    const isHalf = num === fullBowls + 1 && hasHalf;
    const isEmpty = !isFull && !isHalf;

    return { isFull, isHalf, isEmpty };
  };

  return (
    <div className="flex justify-center gap-1.5 sm:gap-2 mt-3 rounded-xl sm:rounded-2xl border border-white/10 bg-black/20 backdrop-blur-xl p-3 sm:p-4">
      {[1, 2, 3, 4, 5].map((num) => {
        const { isFull, isHalf, isEmpty } = getBowlDisplay(num);

        return (
          <div
            key={num}
            className={`cursor-pointer transition-transform hover:scale-110 active:scale-95 relative w-[44px] h-[44px] sm:w-[56px] sm:h-[56px] ${
              isFull ? "opacity-100" : isEmpty ? "opacity-25 grayscale" : ""
            }`}
            onClick={(e) => handleClick(num, e)}
            onMouseMove={(e) => handleMouseMove(num, e)}
            onMouseLeave={() => setHovered(0)}
            onTouchEnd={() => setHovered(0)}
          >
            {isHalf ? (
              <>
                <img
                  src="/ramen-bowl.png"
                  alt="Ramen bowl"
                  className="w-full h-full opacity-25 grayscale pointer-events-none"
                />
                <img
                  src="/ramen-bowl.png"
                  alt="Ramen bowl"
                  className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  style={{
                    clipPath: "inset(0 50% 0 0)",
                    willChange: "clip-path",
                  }}
                />
              </>
            ) : (
              <img
                src="/ramen-bowl.png"
                alt="Ramen bowl"
                className="w-full h-full pointer-events-none"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function AnimeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { animeList, updateRating } = useAnime();
  const { user } = useAuth();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [userRating, setUserRating] = useState(0);
  const [reviewError, setReviewError] = useState("");
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [fetchedAnime, setFetchedAnime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [likedReviews, setLikedReviews] = useState(new Set());
  const [reviewLikeCounts, setReviewLikeCounts] = useState({});
  const [reviewLikeBusyById, setReviewLikeBusyById] = useState({});

  // Try to find anime in context first, otherwise use fetched anime
  const animeFromContext = animeList.find((a) => String(a.AnimeID) === String(id));
  const anime = animeFromContext || fetchedAnime;

  // Fetch anime from API if not in context
  useEffect(() => {
    if (!animeFromContext && id) {
      setIsLoading(true);
      fetchAnimeDetails(id)
        .then((data) => {
          setFetchedAnime(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching anime:", error);
          setIsLoading(false);
        });
    }
  }, [id, animeFromContext]);

  const sanitizedTitle = anime?.Title?.toLowerCase().replace(/[^a-z0-9]/gi, "").trim();
  const fallbackImage = `/images/${sanitizedTitle}.jpg`;

  const imageSrc =
    anime?.PhotoUrl && !anime.PhotoUrl.includes("example.com")
      ? anime.PhotoUrl.startsWith("http")
        ? anime.PhotoUrl
        : anime.PhotoUrl.startsWith("/")
        ? anime.PhotoUrl
        : `/${anime.PhotoUrl.replace(/^\/+/, "")}`
      : fallbackImage;

  useEffect(() => {
    if (anime) {
      fetchAnimeReviews(anime.AnimeID).then(async (reviews) => {
        const mappedReviews = reviews.map((review) => ({
          reviewId: review.review_id,
          userId: review.user_id,
          user: review.username,
          text: review.review_text,
          rating: review.rating,
          date: review.review_date,
        }));
        setComments(mappedReviews);

        // Set user's existing rating if found
        if (user) {
          const userReview = mappedReviews.find((r) => r.userId === user.id);
          if (userReview) setUserRating(userReview.rating);
        }

        // Fetch like status for all reviews
        if (mappedReviews.length > 0) {
          try {
            const items = mappedReviews.map((r) => ({
              target_type: "review",
              target_id: r.reviewId,
            }));

            const res = await fetch("/api/likes/batch", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ items }),
            });

            if (res.ok) {
              const likesData = await res.json();
              const liked = new Set();
              const counts = {};

              likesData.forEach((item) => {
                const canonical = readCanonicalLikeState(item);
                if (canonical.liked_by_viewer) liked.add(parseInt(item.target_id));
                counts[item.target_id] = canonical.like_count;
              });

              setLikedReviews(liked);
              setReviewLikeCounts(counts);
            }
          } catch (err) {
            console.error("Error fetching review likes:", err);
          }
        }
      });
    }
  }, [anime, user]);

  const handleUserRating = (newRating) => {
    // Only update local state, don't submit to API yet
    // Rating will be submitted when user clicks "Post Review"
    setUserRating(newRating);
  };

  const handleEditReview = async (reviewId) => {
    if (editedText.trim().length < 10) {
      setReviewError("Review must be at least 10 characters");
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      const response = await fetch(`${API_BASE}/reviews/${reviewId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          rating: comments.find((c) => c.reviewId === reviewId).rating,
          review_text: editedText,
        }),
      });

      if (response.ok) {
        const reviews = await fetchAnimeReviews(anime.AnimeID);
        const mappedReviews = reviews.map((review) => ({
          reviewId: review.review_id,
          userId: review.user_id,
          user: review.username,
          text: review.review_text,
          rating: review.rating,
          date: review.review_date,
        }));
        setComments(mappedReviews);
        setEditingReviewId(null);
        setEditedText("");
        setReviewError("");
      } else {
        const data = await response.json();
        setReviewError(data.error || "Failed to update review");
      }
    } catch (error) {
      console.error("Error updating review:", error);
      setReviewError("Failed to update review");
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      setReviewError("You must be logged in to post a review");
      return;
    }
    if (userRating === 0) {
      setReviewError("Please rate the anime before posting a review");
      return;
    }
    if (newComment.trim().length < 10) {
      setReviewError("Review must be at least 10 characters");
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      const response = await fetch(`${API_BASE}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          anime_id: anime.AnimeID,
          rating: userRating,
          review_text: newComment,
        }),
      });

      if (response.ok) {
        const reviews = await fetchAnimeReviews(anime.AnimeID);
        const mappedReviews = reviews.map((review) => ({
          reviewId: review.review_id,
          userId: review.user_id,
          user: review.username,
          text: review.review_text,
          rating: review.rating,
          date: review.review_date,
        }));
        setComments(mappedReviews);
        setNewComment("");
        setReviewError("");
      } else {
        const data = await response.json();
        setReviewError(data.error || "Failed to post review");
      }
    } catch (error) {
      console.error("Error posting review:", error);
      setReviewError("Failed to post review");
    }
  };

  const handleLikeReview = async (reviewId) => {
    if (!user) {
      setReviewError("Please log in to like reviews.");
      return;
    }

    if (reviewLikeBusyById[reviewId]) return;
    const optimisticState = buildOptimisticLikeState({
      liked_by_viewer: likedReviews.has(reviewId),
      like_count: reviewLikeCounts[reviewId] || 0,
    });
    setReviewLikeBusyById((prev) => ({ ...prev, [reviewId]: true }));
    setLikedReviews((prev) => {
      const next = new Set(prev);
      if (optimisticState.optimisticLiked) next.add(reviewId);
      else next.delete(reviewId);
      return next;
    });
    setReviewLikeCounts((prev) => ({
      ...prev,
      [reviewId]: optimisticState.optimisticCount,
    }));

    try {
      const res = await fetch("/api/likes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          target_type: "review",
          target_id: reviewId,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const canonical = reconcileLikeStateFromResponse(data, optimisticState);

        // Update liked status
        setLikedReviews((prev) => {
          const newSet = new Set(prev);
          if (canonical.liked_by_viewer) newSet.add(reviewId);
          else newSet.delete(reviewId);
          return newSet;
        });

        // Update like count
        setReviewLikeCounts((prev) => ({
          ...prev,
          [reviewId]: canonical.like_count,
        }));
      } else {
        setLikedReviews((prev) => {
          const rollback = new Set(prev);
          if (optimisticState.prevLiked) rollback.add(reviewId);
          else rollback.delete(reviewId);
          return rollback;
        });
        setReviewLikeCounts((prev) => ({
          ...prev,
          [reviewId]: optimisticState.prevCount,
        }));
      }
    } catch (error) {
      console.error("Error toggling review like:", error);
      setLikedReviews((prev) => {
        const rollback = new Set(prev);
        if (optimisticState.prevLiked) rollback.add(reviewId);
        else rollback.delete(reviewId);
        return rollback;
      });
      setReviewLikeCounts((prev) => ({
        ...prev,
        [reviewId]: optimisticState.prevCount,
      }));
      setReviewError("Failed to update like.");
    } finally {
      setReviewLikeBusyById((prev) => ({ ...prev, [reviewId]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#070b1a] text-white">
        <div className="flex items-center justify-center px-4 py-16">
          <p className="text-white/60 text-center">Loading anime details...</p>
        </div>
      </div>
    );
  }

  // NOT FOUND (Navbar included)
  if (!anime) {
    return (
      <div className="min-h-screen bg-[#070b1a] text-white">
        <div className="flex items-center justify-center px-4 py-16">
          <p className="text-white/60 text-center">Anime not found.</p>
        </div>
      </div>
    );
  }

  const bannerSrc = anime?.BannerUrl || null;

  const getFormatBadge = () => {
    if (!anime?.Format) return null;
    const map = {
      TV: "bg-sky-500/15 border-sky-400/30 text-sky-200",
      MOVIE: "bg-purple-500/15 border-purple-400/30 text-purple-200",
      OVA: "bg-amber-500/15 border-amber-400/30 text-amber-200",
      ONA: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200",
      SPECIAL: "bg-pink-500/15 border-pink-400/30 text-pink-200",
    };
    const cls = map[anime.Format] || "bg-white/10 border-white/15 text-white/80";

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cls}`}
      >
        {anime.Format === "TV" && <Tv size={12} />}
        {anime.Format === "MOVIE" && <Film size={12} />}
        {anime.Format}
      </span>
    );
  };

  const getStatusBadge = () => {
    if (!anime?.Status) return null;
    const map = {
      RELEASING: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200",
      FINISHED: "bg-sky-500/15 border-sky-400/30 text-sky-200",
      NOT_YET_RELEASED: "bg-amber-500/15 border-amber-400/30 text-amber-200",
      CANCELLED: "bg-red-500/15 border-red-400/30 text-red-200",
    };
    const cls = map[anime.Status] || "bg-white/10 border-white/15 text-white/80";

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cls}`}
      >
        {anime.Status.replaceAll("_", " ")}
      </span>
    );
  };

  const getRightStufLink = (animeTitle) => {
    const affiliateId = "YOUR_RIGHTSTUF_ID";
    const encodedTitle = encodeURIComponent(animeTitle);
    return `https://www.rightstufanime.com/search?keywords=${encodedTitle}&aff=${affiliateId}`;
  };

  return (
    <div className="min-h-screen bg-[#070b1a] text-white">
      {/* Scrollbar hide (applies to nested scroll containers too) */}
      <style>{`
        .scroll-hide { scrollbar-width: none; -ms-overflow-style: none; }
        .scroll-hide::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
        .scroll-hide * { scrollbar-width: none; -ms-overflow-style: none; }
        .scroll-hide *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      `}</style>

      <div className="relative">
        {bannerSrc && (
          <div className="relative w-full h-48 sm:h-64 md:h-80 overflow-hidden sm:mt-0">
            <img
              src={bannerSrc}
              alt={`${anime.Title} banner`}
              className="w-full h-full object-cover opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-[#070b1a]/75 to-[#070b1a]" />
          </div>
        )}

        <div className="absolute top-[calc(1.75rem+env(safe-area-inset-top))] left-3 sm:left-4 z-30">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg sm:rounded-xl border border-white/12 bg-black/40 backdrop-blur-xl px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-white/85 hover:text-white hover:bg-black/55 transition"
          >
            <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="text-xs sm:text-sm font-medium">Back</span>
          </button>
        </div>

        <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1.2fr] gap-6 sm:gap-8 lg:gap-10 items-start">
            {/* LEFT */}
            <div className="space-y-4 sm:space-y-6">
              {/* Updated header to be sleeker like your reference */}
              <div
                className="
                  relative overflow-hidden
                  rounded-[26px] sm:rounded-[34px]
                  border border-white/10
                  bg-[#0b1022]/55
                  backdrop-blur-xl
                  px-5 py-6
                  sm:px-8 sm:py-10
                  shadow-[0_30px_120px_rgba(0,0,0,0.55)]
                "
              >
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-black/30" />
                <div className="pointer-events-none absolute inset-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />

                <div className="relative flex flex-col items-center text-center gap-3 sm:gap-4">
                  <p className="text-[10px] tracking-[0.36em] uppercase text-white/45">
                    Anime Details
                  </p>

                  <h1 className="flex items-center justify-center gap-3 flex-wrap">
                    <span className="inline-flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/25">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-200" />
                    </span>

                    <span className="text-[34px] leading-[1.05] sm:text-5xl font-extrabold tracking-tight text-emerald-100">
                      {anime.Title}
                    </span>
                  </h1>

                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {getFormatBadge()}
                    {getStatusBadge()}
                  </div>

                  {anime.TitleEnglish && anime.TitleEnglish !== anime.Title && (
                    <p className="text-sm sm:text-base text-white/55 italic px-2">
                      {anime.TitleEnglish}
                    </p>
                  )}

                  {anime.TitleNative && (
                    <p className="text-[13px] sm:text-sm text-white/50">
                      {anime.TitleNative}
                    </p>
                  )}
                </div>
              </div>

              {/* Poster + info panel styled closer to your reference */}
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-4 sm:gap-6 items-start">
                <img
                  src={imageSrc}
                  alt={anime.Title}
                  className="w-full md:w-[280px] lg:w-[320px] h-[360px] sm:h-[420px] md:h-[420px] object-cover rounded-[22px] sm:rounded-[28px] border border-white/10 shadow-[0_18px_60px_rgba(0,0,0,0.55)] mx-auto"
                />

                <div
                  className="
                    relative overflow-hidden
                    rounded-[22px] sm:rounded-[28px]
                    border border-white/10
                    bg-[#0b1022]/55
                    backdrop-blur-xl
                    p-5 sm:p-6
                    shadow-[0_18px_60px_rgba(0,0,0,0.45)]
                  "
                >
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.05] via-transparent to-black/25" />
                  <div className="pointer-events-none absolute inset-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />

                  <div className="relative">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      {anime.Studio && (
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.26em] text-white/40">
                            Studio
                          </p>
                          <p
                            className="mt-1 text-sm sm:text-base text-white/90 font-semibold truncate"
                            title={anime.Studio}
                          >
                            {anime.Studio}
                          </p>
                        </div>
                      )}

                      {anime.Episodes && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.26em] text-white/40">
                            Episodes
                          </p>
                          <p className="mt-1 text-sm sm:text-base text-white/90 font-semibold">
                            {anime.Episodes} eps
                          </p>
                        </div>
                      )}

                      {anime.EpisodeDuration && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.26em] text-white/40 inline-flex items-center gap-1.5">
                            <Clock size={12} className="text-white/40" /> Duration
                          </p>
                          <p className="mt-1 text-sm sm:text-base text-white/90 font-semibold">
                            {anime.EpisodeDuration} min
                          </p>
                        </div>
                      )}

                      {anime.Season && anime.SeasonYear && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.26em] text-white/40 inline-flex items-center gap-1.5">
                            <Calendar size={12} className="text-white/40" /> Season
                          </p>
                          <p className="mt-1 text-sm sm:text-base text-white/90 font-semibold uppercase">
                            {anime.Season} {anime.SeasonYear}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-5 text-[13px] sm:text-sm text-white/70 leading-relaxed">
                      {anime.Description || "No description available."}
                    </div>
                  </div>
                </div>
              </div>

              {/* Characters & Voice Actors */}
              <CharactersVoiceActors animeId={anime.AnimeID} />

              {/* Ratings */}
              <div className="rounded-2xl sm:rounded-3xl border border-white/12 bg-white/7 backdrop-blur-xl p-3 sm:p-4 text-center shadow-[0_25px_90px_rgba(0,0,0,0.35)]">
                <h3 className="text-base sm:text-lg font-semibold text-white">Ratings</h3>

                <div className="mt-3 space-y-1.5">
                  <p className="text-xs sm:text-sm text-white/55">Community Rating</p>
                  <div className="flex justify-center">
                    <RatingStars rating={anime.AverageRating} />
                  </div>
                </div>

                {anime.AniListRating && (
                  <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5">
                    <p className="text-xs sm:text-sm text-white/55">AniList Score</p>
                    <div className="flex items-center justify-center gap-2">
                      <Star className="text-amber-300 fill-amber-300" size={18} />
                      <span className="text-xl sm:text-2xl font-bold text-amber-200">
                        {Number(anime.AniListRating).toFixed(1)}
                      </span>
                      <span className="text-white/50 text-sm">/ 10</span>
                    </div>
                  </div>
                )}

                {user ? (
                  <div className="mt-3 border-t border-white/10 pt-3">
                    <p className="text-xs sm:text-sm text-white/55 mb-1.5">Your Rating</p>
                    <RamenRatingSystem
                      key={userRating}
                      onRate={handleUserRating}
                      initialRating={userRating}
                    />
                    <p className="text-[10px] sm:text-xs text-white/45 mt-1.5">
                      Click left for half bowl, right for full bowl — 5 bowls = elite.
                    </p>
                  </div>
                ) : (
                  <p className="text-white/55 mt-3 text-xs sm:text-sm border-t border-white/10 pt-3">
                    Sign in to rate this anime.
                  </p>
                )}
              </div>

              {/* Watch On */}
              {anime.StreamingPlatforms && anime.StreamingPlatforms.length > 0 && (
                <div className="rounded-2xl sm:rounded-3xl border border-white/12 bg-white/7 backdrop-blur-xl p-3 sm:p-4 shadow-[0_25px_90px_rgba(0,0,0,0.35)]">
                  <h3 className="text-base sm:text-lg font-bold text-white flex items-center justify-center gap-2">
                    <Tv size={14} className="sm:w-4 sm:h-4 text-emerald-200" />
                    Watch On
                  </h3>
                  <div className="mt-2.5 sm:mt-3 flex flex-wrap gap-2 justify-center">
                    {anime.StreamingPlatforms.map((platform, index) => (
                      <a
                        key={index}
                        href={platform.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-emerald-400/25 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/15 hover:border-emerald-300/40 active:scale-95 transition"
                      >
                        {platform.site}
                        <ExternalLink size={12} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Genres & Tags */}
              {((anime.Genres && anime.Genres.length > 0) ||
                (anime.Tags && anime.Tags.length > 0)) && (
                <div className="rounded-xl border border-white/12 bg-white/5 backdrop-blur-xl p-3 sm:p-4 shadow-[0_15px_50px_rgba(0,0,0,0.25)]">
                  <div className="space-y-2">
                    {anime.Genres && anime.Genres.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">
                          Genres
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {anime.Genres.map((genre, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium border border-purple-400/20 bg-purple-400/8 text-purple-200"
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {anime.Tags && anime.Tags.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">
                          Tags
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {anime.Tags.slice(0, 8).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 rounded-md text-[10px] sm:text-xs border border-cyan-400/20 bg-cyan-400/8 text-cyan-200"
                            >
                              {tag}
                            </span>
                          ))}
                          {anime.Tags.length > 8 && (
                            <span className="text-[10px] text-white/35 px-2 py-0.5">
                              +{anime.Tags.length - 8}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT */}
            <aside className="space-y-6 lg:sticky lg:top-24">
              {/* Updated Episodes panel */}
              {anime.Episodes && (
                <div
                  className="
                    relative overflow-hidden
                    rounded-2xl sm:rounded-3xl
                    border border-white/12
                    bg-white/[0.06]
                    backdrop-blur-xl
                    shadow-[0_22px_80px_rgba(0,0,0,0.55)]
                  "
                >
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/25" />
                  <div className="pointer-events-none absolute inset-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />

                  <div className="relative flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-white/10">
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                        Episodes
                      </p>
                      <h3 className="mt-1 flex items-center gap-2 text-base sm:text-lg font-semibold text-white">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-black/20">
                          <Play size={16} className="text-emerald-200" />
                        </span>
                        <span className="truncate">Episode Guide</span>
                      </h3>
                    </div>

                    <div
                      className="
                        shrink-0
                        inline-flex items-center
                        rounded-full
                        border border-white/10
                        bg-black/20
                        px-2.5 py-1
                        text-[11px] font-semibold
                        text-white/75
                      "
                      title={`${anime.Episodes} episodes`}
                    >
                      {anime.Episodes} eps
                    </div>
                  </div>

                  <div className="relative">
                    <div className="pointer-events-none absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-[#070b1a] to-transparent z-10" />
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#070b1a] to-transparent z-10" />

                    <div
                      className="
                        max-h-[360px] sm:max-h-[520px] lg:max-h-[560px]
                        overflow-y-auto scroll-hide
                        px-3 sm:px-5
                        py-3 sm:py-4
                      "
                    >
                      <div
                        className="
                          rounded-2xl
                          border border-white/10
                          bg-black/20
                          p-3 sm:p-4
                          shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]
                        "
                      >
                        <EpisodeList animeId={anime.AnimeID} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reviews */}
              <div className="rounded-3xl border border-white/12 bg-white/7 backdrop-blur-xl p-6 space-y-4 shadow-[0_25px_90px_rgba(0,0,0,0.45)]">
                <h3 className="text-lg font-bold text-white flex items-center gap-2 border-b border-white/10 pb-3">
                  <Users className="text-emerald-200" size={18} />
                  Community Reviews
                </h3>

                <div className="space-y-3 max-h-[520px] overflow-y-auto scroll-hide pr-1">
                  {comments.length > 0 ? (
                    comments.map((comment, index) => (
                      <div
                        key={index}
                        className="rounded-2xl border border-white/10 bg-black/22 p-4 space-y-2 hover:bg-white/5 transition shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-emerald-200 font-semibold">
                            {comment.user}
                          </p>

                          {user && user.id === comment.userId && (
                            <button
                              onClick={() => {
                                if (editingReviewId === comment.reviewId) {
                                  setEditingReviewId(null);
                                  setEditedText("");
                                } else {
                                  setEditingReviewId(comment.reviewId);
                                  setEditedText(comment.text || "");
                                }
                              }}
                              className="flex items-center gap-1 text-white/45 hover:text-amber-300 transition-colors text-xs"
                            >
                              {editingReviewId === comment.reviewId ? (
                                <>
                                  <X size={12} /> Cancel
                                </>
                              ) : (
                                <>
                                  <Edit2 size={12} /> Edit
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        {editingReviewId === comment.reviewId ? (
                          <div className="space-y-2">
                            <textarea
                              value={editedText}
                              onChange={(e) => setEditedText(e.target.value)}
                              className="w-full min-h-[80px] rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white/85 placeholder:text-white/35 focus:outline-none focus:border-amber-400/35 transition"
                            />
                            <button
                              onClick={() => handleEditReview(comment.reviewId)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-400/25 bg-amber-400/10 text-amber-200 hover:bg-amber-400/15 transition"
                            >
                              <Save size={12} /> Save Changes
                            </button>
                          </div>
                        ) : (
                          <p className="text-white/80 text-sm leading-snug">
                            {comment.text}
                          </p>
                        )}

                        {editingReviewId !== comment.reviewId && (
                          <div className="flex gap-3 text-white/45 text-xs">
                            <button
                              onClick={() => handleLikeReview(comment.reviewId)}
                              disabled={!!reviewLikeBusyById[comment.reviewId]}
                              className={`flex items-center gap-1 transition-colors ${
                                "text-white/70 hover:text-white/90"
                              } ${reviewLikeBusyById[comment.reviewId] ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                              <Heart
                                size={12}
                                className={likedReviews.has(comment.reviewId) ? "text-emerald-300" : ""}
                                fill={
                                  likedReviews.has(comment.reviewId) ? "currentColor" : "none"
                                }
                              />
                              {reviewLikeCounts[comment.reviewId] || 0}
                            </button>

                            <button className="flex items-center gap-1 hover:text-emerald-200 transition-colors">
                              <MessageCircle size={12} /> Reply
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-white/55 text-sm">No reviews yet. Be the first!</p>
                  )}
                </div>

                {user ? (
                  <div className="pt-2 space-y-2">
                    {reviewError && (
                      <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                        {reviewError}
                      </div>
                    )}

                    {userRating === 0 && (
                      <p className="text-amber-200 text-xs">
                        Rate the anime above before posting a review.
                      </p>
                    )}

                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a review (minimum 10 characters)..."
                      className="w-full min-h-[92px] rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/85 placeholder:text-white/35 focus:outline-none focus:border-emerald-400/35 transition"
                    />
                    <p className="text-white/40 text-xs">
                      {newComment.length}/10 characters minimum
                    </p>

                    <button
                      onClick={handleAddComment}
                      disabled={userRating === 0 || newComment.trim().length < 10}
                      className="w-full rounded-xl border border-emerald-400/25 bg-emerald-400/12 hover:bg-emerald-400/16 text-emerald-100 py-2.5 text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Post Review
                    </button>
                  </div>
                ) : (
                  <p className="text-white/55 text-xs pt-2">Sign in to leave a review.</p>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnimeDetails;
